import { useQuery } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";

import { listVerifiedHomeReviews } from "@/lib/home-reviews.functions";

/**
 * Opiniones verificadas. Sólo se muestran reseñas reales aprobadas en la base
 * de datos (sin filas sembradas). Si no hay ninguna, la sección no se dibuja.
 */
export function VerifiedReviews() {
  const { t, i18n } = useTranslation();
  const { data } = useQuery({
    queryKey: ["home", "verified-reviews"],
    queryFn: () => listVerifiedHomeReviews(),
    staleTime: 10 * 60 * 1000,
  });

  const reviews = data?.reviews ?? [];
  if (reviews.length === 0) return null;

  const dateLocale = i18n.language === "es" ? "es-MX" : i18n.language;

  return (
    <section id="reviews" aria-label={t("homeV2.reviewsEyebrow")} className="bg-white">
      <div className="mx-auto max-w-[1360px] px-4 py-12 sm:px-6 lg:px-8">
        <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
          {t("homeV2.reviewsEyebrow")}
        </span>
        <h2 className="mt-1.5 font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {t("homeV2.reviewsTitle")}
        </h2>

        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r) => (
            <li
              key={r.id}
              className="rounded-[20px] border border-border/70 bg-[#FFFDF7] p-5 shadow-[0_12px_28px_-22px_rgba(23,23,23,0.4)]"
            >
              {r.rating != null && (
                <div
                  className="flex items-center gap-0.5 text-primary"
                  aria-label={t("homeV2.ratingOf", { n: r.rating })}
                >
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </div>
              )}
              <blockquote className="mt-3 text-[15px] leading-relaxed text-foreground">
                “{r.comment}”
              </blockquote>
              <figcaption className="mt-4 text-[12.5px] text-muted-foreground">
                {r.name && <span className="font-semibold text-foreground">{r.name}</span>}
                {r.createdAt && (
                  <>
                    {r.name ? " · " : ""}
                    {new Date(r.createdAt).toLocaleDateString(dateLocale, {
                      year: "numeric",
                      month: "long",
                    })}
                  </>
                )}
                {r.tourTitle && <div className="mt-1 line-clamp-1">{r.tourTitle}</div>}
                <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-primary">
                  {t("homeV2.reviewVerified")}
                </div>
              </figcaption>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
